class ProductPage {
  constructor() {
    this.container = document.querySelector(".product-page");
    if (!this.container) return;

    this.moneyFormat = this.container.dataset.moneyFormat || "{{amount}} €";
    this.form = this.container.querySelector(".product-page__form");
    this.variantInput = this.form?.querySelector("[data-variant-id]");
    this.colorSwatches = this.container.querySelectorAll("[data-color]");
    this.sizeButtons = this.container.querySelectorAll("[data-size]");
    this.optionButtons = this.container.querySelectorAll("[data-option-value]");
    this.quantityInput = this.container.querySelector("[data-quantity-input]");
    this.quantityDecrease = this.container.querySelector("[data-quantity-decrease]");
    this.quantityIncrease = this.container.querySelector("[data-quantity-increase]");
    this.priceElement = this.container.querySelector(".product-page__price");
    this.addToCartButton = this.container.querySelector(".product-page__add-to-cart");
    this.mainImage = this.container.querySelector(".product-page__media img");
    this.mediaContainer = this.container.querySelector(".product-page__media");
    this.galleryItems = this.container.querySelectorAll(".product-page__gallery-item");
    this.featuredImageUrl = this.mediaContainer?.dataset.featuredImageUrl || null;
    this.accordionSummaries = this.container.querySelectorAll(".product-page__accordion-summary");

    this.variants = this.#parseVariants();
    this.images = this.#parseImages();
    this.currentVariantPrice = this.#getCurrentVariantPrice();
    this.#hideVariantImages();
    this.#bindEvents();
    this.#updateColorAvailability();
    this.#updateInitialImage();
  }

  #parseVariants() {
    const variantsJson = this.form?.querySelector("[data-variants-json]");
    if (!variantsJson) return [];

    try {
      return JSON.parse(variantsJson.textContent);
    } catch {
      return [];
    }
  }

  #parseImages() {
    const imagesJson = this.form?.querySelector("[data-images-json]");
    if (!imagesJson) return [];

    try {
      return JSON.parse(imagesJson.textContent);
    } catch {
      return [];
    }
  }

  #getCurrentVariantPrice() {
    if (!this.variantInput || !this.variants.length) return 0;

    const currentVariantId = parseInt(this.variantInput.value);
    const currentVariant = this.variants.find((v) => v.id === currentVariantId);

    return currentVariant?.price || 0;
  }

  #updateInitialImage() {
    if (!this.variantInput || !this.variants.length) return;

    const currentVariantId = parseInt(this.variantInput.value);
    const currentVariant = this.variants.find((v) => v.id === currentVariantId);

    if (currentVariant) {
      this.#updateImage(currentVariant);
    }
  }

  #hideVariantImages() {
    if (!this.variants.length || !this.galleryItems.length) return;

    const variantImageIds = new Set();
    let firstVariantImageId = null;

    for (const variant of this.variants) {
      const imageId = variant.image_id || variant.image?.id || variant.featured_image?.id;
      if (imageId) {
        const numImageId = Number(imageId);
        variantImageIds.add(numImageId);
        if (firstVariantImageId === null) {
          firstVariantImageId = numImageId;
        }
      }
    }

    for (const galleryItem of this.galleryItems) {
      const galleryImageId = parseInt(galleryItem.dataset.imageId, 10);
      if (variantImageIds.has(galleryImageId) && galleryImageId !== firstVariantImageId) {
        galleryItem.style.display = "none";
      }
    }
  }

  #bindEvents() {
    for (const item of this.galleryItems) {
      item.addEventListener("click", (e) => this.#handleGalleryItemClick(e));
    }

    for (const swatch of this.colorSwatches) {
      swatch.addEventListener("click", (e) => this.#handleColorSelect(e));
    }

    for (const button of this.sizeButtons) {
      button.addEventListener("click", (e) => this.#handleSizeSelect(e));
    }

    for (const button of this.optionButtons) {
      button.addEventListener("click", (e) => this.#handleOptionSelect(e));
    }

    this.quantityDecrease?.addEventListener("click", () => this.#updateQuantity(-1));
    this.quantityIncrease?.addEventListener("click", () => this.#updateQuantity(1));
    this.quantityInput?.addEventListener("change", () => this.#validateQuantity());

    for (const summary of this.accordionSummaries) {
      summary.addEventListener("click", (e) => this.#handleAccordionToggle(e));
    }
  }

  #handleGalleryItemClick(e) {
    e.preventDefault();

    const item = e.currentTarget;
    const imageUrl = item.dataset.imageUrl;
    if (!imageUrl || !this.mainImage) return;

    this.mainImage.src = imageUrl;
    this.mainImage.srcset = "";

    for (const galleryItem of this.galleryItems) {
      galleryItem.classList.toggle("product-page__gallery-item--active", galleryItem === item);
    }

    this.mainImage.closest(".product-page__media")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  #handleColorSelect(e) {
    e.preventDefault();

    const swatch = e.currentTarget;
    if (swatch.disabled || swatch.classList.contains("is-disabled")) return;

    const { color, optionPosition } = swatch.dataset;
    if (!color || !optionPosition) return;

    const optionPos = parseInt(optionPosition);

    this.#setActiveElement(this.colorSwatches, swatch);

    const colorVariant = this.variants.find((variant) => {
      const variantColor = variant[`option${optionPos}`];
      return variantColor === color;
    });

    if (colorVariant) {
      this.#updateImage(colorVariant);
    }

    this.#updateVariantSelection(optionPos, color);
  }

  #handleSizeSelect(e) {
    e.preventDefault();

    const button = e.currentTarget;
    const { size, optionPosition } = button.dataset;
    if (!size || !optionPosition) return;

    this.#updateVariantSelection(parseInt(optionPosition), size);
    this.#setActiveElement(this.sizeButtons, button);
    this.#updateColorAvailability();
  }

  #handleOptionSelect(e) {
    e.preventDefault();

    const button = e.currentTarget;
    const { optionValue, optionPosition } = button.dataset;
    if (!optionValue || !optionPosition) return;

    const optionPos = parseInt(optionPosition);
    const optionGroup = button.closest(".product-page__option");
    const buttons = optionGroup?.querySelectorAll("[data-option-value]") || [];

    this.#updateVariantSelection(optionPos, optionValue);
    this.#setActiveElement(buttons, button);
    this.#updateColorAvailability();
  }

  #updateVariantSelection(optionPosition, value) {
    if (!this.variants.length || !this.variantInput) return;

    const currentOptions = this.#getCurrentOptions();
    currentOptions[optionPosition - 1] = value;

    const matchingVariant = this.variants.find(
      (variant) =>
        variant.option1 === currentOptions[0] &&
        variant.option2 === (currentOptions[1] || null) &&
        variant.option3 === (currentOptions[2] || null)
    );

    if (!matchingVariant) return;

    this.variantInput.value = matchingVariant.id;
    this.#updatePrice(matchingVariant);
    this.#updateAvailability(matchingVariant);
    this.#updateImage(matchingVariant);
  }

  #updateColorAvailability() {
    if (!this.colorSwatches.length) return;

    const currentOptions = this.#getCurrentOptions();
    const sizeOptionPosition = this.#getSizeOptionPosition();
    const selectedSize = sizeOptionPosition ? currentOptions[sizeOptionPosition - 1] : null;
    const selectedColorSwatch = this.container.querySelector("[data-color].is-selected");
    const selectedColor = selectedColorSwatch?.dataset.color;

    let firstAvailableSwatch = null;

    for (const swatch of this.colorSwatches) {
      const color = swatch.dataset.color;
      const colorOptionPosition = parseInt(swatch.dataset.optionPosition);

      if (!color) continue;

      let isAvailable = false;

      for (const variant of this.variants) {
        const variantColor = variant[`option${colorOptionPosition}`];
        const variantSize = sizeOptionPosition ? variant[`option${sizeOptionPosition}`] : null;

        if (variantColor === color && variant.available) {
          if (!selectedSize || variantSize === selectedSize) {
            isAvailable = true;
            break;
          }
        }
      }

      swatch.disabled = !isAvailable;
      swatch.classList.toggle("is-disabled", !isAvailable);

      if (isAvailable && !firstAvailableSwatch) {
        firstAvailableSwatch = swatch;
      }
    }

    if (selectedColorSwatch && selectedColorSwatch.disabled && firstAvailableSwatch) {
      this.#setActiveElement(this.colorSwatches, firstAvailableSwatch);
      const color = firstAvailableSwatch.dataset.color;
      const optionPos = parseInt(firstAvailableSwatch.dataset.optionPosition);
      this.#updateVariantSelection(optionPos, color);
    }
  }

  #getSizeOptionPosition() {
    const selectedSize = this.container.querySelector("[data-size].is-selected");
    if (selectedSize) {
      return parseInt(selectedSize.dataset.optionPosition);
    }
    return null;
  }

  #getCurrentOptions() {
    const options = [null, null, null];

    const selectedColor = this.container.querySelector("[data-color].is-selected");
    if (selectedColor) {
      const pos = parseInt(selectedColor.dataset.optionPosition);
      options[pos - 1] = selectedColor.dataset.color;
    }

    const selectedSize = this.container.querySelector("[data-size].is-selected");
    if (selectedSize) {
      const pos = parseInt(selectedSize.dataset.optionPosition);
      options[pos - 1] = selectedSize.dataset.size;
    }

    const selectedOption = this.container.querySelector("[data-option-value].is-selected");
    if (selectedOption) {
      const pos = parseInt(selectedOption.dataset.optionPosition);
      options[pos - 1] = selectedOption.dataset.optionValue;
    }

    return options;
  }

  #setActiveElement(elements, activeElement) {
    for (const element of elements) {
      element.classList.toggle("is-selected", element === activeElement);
    }
  }

  #formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, this.moneyFormat);
    }

    const valueInDollars = cents / 100;
    const price = valueInDollars.toFixed(2);
    const priceInt = Math.round(valueInDollars);

    // Format with thousands separator (dot) and comma for decimals
    const priceWithThousands = price.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const priceWithComma = priceWithThousands.replace(/\.(\d{2})$/, ",$1");

    // Format integer with thousands separator
    const priceIntWithThousands = priceInt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return this.moneyFormat
      .replace("{{amount}}", price)
      .replace("{{amount_no_decimals}}", priceInt)
      .replace("{{amount_with_comma_separator}}", priceWithComma)
      .replace("{{amount_no_decimals_with_comma_separator}}", priceIntWithThousands);
  }

  #updatePrice(variant) {
    if (!this.priceElement || !variant.price) return;

    this.currentVariantPrice = variant.price;

    const quantity = parseInt(this.quantityInput?.value) || 1;
    const totalPrice = variant.price * quantity;

    this.priceElement.textContent = this.#formatMoney(totalPrice);
  }

  #updateAvailability(variant) {
    this.addToCartButton?.toggleAttribute("disabled", !variant.available);
  }

  #updateImage(variant) {
    if (!this.mainImage) return;

    const imageId = variant.image_id || variant.image?.id || variant.featured_image?.id || null;

    if (!imageId) {
      if (this.featuredImageUrl) {
        this.mainImage.src = this.featuredImageUrl;
        this.mainImage.srcset = "";
      }
      return;
    }

    let variantImage = this.images.find((img) => Number(img.id) === Number(imageId));

    if (!variantImage && variant.featured_image?.src) {
      this.mainImage.src = variant.featured_image.src;
      this.mainImage.srcset = "";

      for (const galleryItem of this.galleryItems) {
        const galleryImageId = parseInt(galleryItem.dataset.imageId, 10);
        galleryItem.classList.toggle("product-page__gallery-item--active", Number(galleryImageId) === Number(imageId));
      }
      return;
    }

    if (!variantImage) {
      const galleryItem = Array.from(this.galleryItems).find((item) => {
        const galleryImageId = parseInt(item.dataset.imageId, 10);
        return Number(galleryImageId) === Number(imageId);
      });

      if (galleryItem) {
        const imageUrl = galleryItem.dataset.imageUrl;
        if (imageUrl) {
          this.mainImage.src = imageUrl;
          this.mainImage.srcset = "";

          for (const item of this.galleryItems) {
            item.classList.toggle("product-page__gallery-item--active", item === galleryItem);
          }
          return;
        }
      }

      if (this.featuredImageUrl) {
        this.mainImage.src = this.featuredImageUrl;
        this.mainImage.srcset = "";
      }
      return;
    }

    this.mainImage.src = variantImage.url;
    this.mainImage.srcset = "";

    for (const galleryItem of this.galleryItems) {
      const galleryImageId = parseInt(galleryItem.dataset.imageId, 10);
      galleryItem.classList.toggle("product-page__gallery-item--active", Number(galleryImageId) === Number(imageId));
    }
  }

  #updateQuantity(delta) {
    if (!this.quantityInput) return;

    const currentValue = parseInt(this.quantityInput.value) || 1;
    this.quantityInput.value = Math.max(1, currentValue + delta);
    this.#updatePriceByQuantity();
  }

  #validateQuantity() {
    if (!this.quantityInput) return;

    const value = parseInt(this.quantityInput.value);
    if (isNaN(value) || value < 1) {
      this.quantityInput.value = 1;
    }
    this.#updatePriceByQuantity();
  }

  #updatePriceByQuantity() {
    if (!this.currentVariantPrice || !this.priceElement) return;

    const quantity = parseInt(this.quantityInput?.value) || 1;
    const totalPrice = this.currentVariantPrice * quantity;

    this.priceElement.textContent = this.#formatMoney(totalPrice);
  }

  #handleAccordionToggle(e) {
    const summary = e.currentTarget;
    const accordionItem = summary.closest(".product-page__accordion-item");
    if (!accordionItem) return;

    const isExpanded = accordionItem.classList.contains("is-open");
    accordionItem.classList.toggle("is-open", !isExpanded);
    summary.setAttribute("aria-expanded", !isExpanded);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new ProductPage());
} else {
  new ProductPage();
}
